// Helpers that merge the admin-defined destination groups into the flat
// `allCountries` list so a group (e.g. Indonesia / Indonesia Lite / Premium)
// shows as ONE card and expands into a picker modal on tap.
//
// Shape of a group doc from the backend:
//   { id, name, primary_country_id, members: [{country_id, label, descriptor}] }

// Build a Map<country_id, group> so we can look up a group by any of its members.
export const buildGroupLookup = (groups) => {
  const map = new Map();
  (groups || []).forEach((g) => {
    (g.members || []).forEach((m) => {
      if (m.country_id) map.set(m.country_id, g);
    });
  });
  return map;
};

// Collapse `countries` so every country that's a non-primary member of a
// group is removed. The primary member stays and is tagged with `_group`.
// When a `startingPrices` map (country_id -> AUD) is supplied, the group's
// cheapest member price is attached as `_groupMinPrice` so cards can show
// a single "from $X" badge across all variants.
export const collapseByGroups = (countries, groups, startingPrices = {}) => {
  if (!groups || groups.length === 0) return countries;
  const byMember = buildGroupLookup(groups);
  const kept = [];
  const seenGroupPrimary = new Set();
  for (const c of countries) {
    const g = byMember.get(c.id);
    if (!g) { kept.push(c); continue; }
    if (c.id !== g.primary_country_id) continue; // hide variants, show primary only
    if (seenGroupPrimary.has(g.id)) continue;    // de-dup
    seenGroupPrimary.add(g.id);
    const memberPrices = (g.members || [])
      .map((m) => startingPrices[m.country_id])
      .filter((v) => typeof v === "number" && v > 0);
    const minPrice = memberPrices.length ? Math.min(...memberPrices) : null;
    kept.push({ ...c, _group: g, name: g.name || c.name, _groupMinPrice: minPrice });
  }
  return kept;
};

// Resolve a group's full member list into enriched country objects using the
// loaded `allCountries` list. Returns an array ready for the picker UI.
export const resolveGroupMembers = (group, allCountries) => {
  if (!group) return [];
  const byId = new Map(allCountries.map((c) => [c.id, c]));
  return (group.members || [])
    .map((m) => ({ ...m, country: byId.get(m.country_id) }))
    .filter((m) => m.country);
};
